import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { FuelConsumption, PentaDashboard } from '@volvogroup-internal/dashboard';
import { useTranslation } from 'react-i18next';

import Table from '@/components/Table';
import { getToken } from '@/config/MsalAuthConfig';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useSessionStorage from '@/hooks/useLocalStorage';
import { getAllFleetUnitsQueryOptions, getFuelConsumptionByMonthQueryOptions } from '@/service/vpc/queryOptions';
import { client } from '@/service/vpc/vpcService';
import useFuelConsumptionTable, { mapFuelConsumptionTableData } from '@/tables/useFuelConsumptionTable';
import { chassis } from '@/utils';

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/dashboard/fuel-consumption/')({
  component: FuelConsumptionPage,
});

function FuelConsumptionPage() {
  const { fleetId } = useParams({
    from: '/_authenticated/_account/fleets/$fleetId/dashboard/fuel-consumption/',
  });
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const { value: filterYear } = useSessionStorage('vpckey-filter-year', new Date().getFullYear());
  const { value: filterMonth } = useSessionStorage('vpckey-filter-month', new Date().getMonth() + 1);

  const { isLoading: isFleetUnitsLoading, fleetAccountUnits } = useFleetAccount();
  const { columns } = useFuelConsumptionTable();

  const { data: fleetUnits, isLoading: isfleetUnitsLoading } = useQuery(getAllFleetUnitsQueryOptions({ fleetId }));

  const preFilterUnits = fleetUnits?.items.map((unit) => Number(unit.endCustomerUnitId)) || [];
  const filteredUnits = fleetAccountUnits?.filter((unit) => preFilterUnits?.includes(unit.endCustomerUnitId));

  const chassisInfoFromUnits = chassis.extractChassisIds(filteredUnits || []);

  const { data, isLoading } = useQuery(
    getFuelConsumptionByMonthQueryOptions({
      chassisIds: chassisInfoFromUnits.slice(0, 50).join(','),
      year: filterYear,
      month: filterMonth.toString(),
    })
  );

  // Create a map of chassis ID to unit name
  const tableData = mapFuelConsumptionTableData(data, filteredUnits || []);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <PentaDashboard
          client={{
            httpClient: {
              instance: client,
              getToken: getToken,
            },
            queryClient,
          }}
          language={i18n.language}
          preFilterUnits={preFilterUnits}
          showFilterMenu={true}
          showFilterBar={true}
        >
          <FuelConsumption type="monthly" />
        </PentaDashboard>
      </div>
      <Table
        headerTitle={t(($) => $.pages.account.dashboard.fuel_consumption.details_title)}
        isLoading={isFleetUnitsLoading || isfleetUnitsLoading || isLoading}
        data={tableData}
        columns={columns}
        showSearch={true}
        showPagination={false}
        enableExpanding={true}
        subRowsKey="subRows"
      />
    </div>
  );
}
